import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, ChevronLeft, ChevronRight, RotateCw,
    ImageIcon, Video, FileText,
    Trash2, Save, PlayCircle, PanelLeftClose,
    PanelLeftOpen, X, Palette, Loader2, Group, List, SlidersHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { directorApiClient } from '@/services/directorApi';
import DirectorStudioLayout from './DirectorStudioLayout';

const MISSING_SOURCE_PHOTO_PREFIX = '__missing_source_photo__:';

const normalizeDirectorMediaUrl = (url) => {
    if (typeof url !== 'string' || !url) return '';
    if (
        url.startsWith('http://')
        || url.startsWith('https://')
        || url.startsWith('data:')
        || url.startsWith('blob:')
    ) {
        return url;
    }
    if (url.startsWith('/')) {
        return `${directorApiClient.baseUrl}${url}`;
    }
    return url;
};

const getPhotoReferenceCandidates = (photo) => {
    if (!photo || typeof photo !== 'object') return [];
    return [
        photo.photo_name_id,
        photo.photo_id,
        photo.id,
        photo.source_photo_id,
        photo.source_photo_name_id,
        photo.selected_photo_id,
    ].filter((value) => typeof value === 'string' && value.length > 0);
};

const getPhotoReferenceValue = (photo) => getPhotoReferenceCandidates(photo)[0] || '';

const getPhotoImageUrl = (photo) => normalizeDirectorMediaUrl(
    photo?.data_url || photo?.url || photo?.photo_url || ''
);

const StudioPhase = ({
    scriptData,
    selectedSceneIndex,
    setSelectedSceneIndex,
    onLocalUpdate,
    onSaveScene,
    onGenerateFrame,
    onGenerateSlide,
    onGenerateSceneVideo,
    onGenerateScript,
    onSaveAllScenes,
    loading,
    saveLoading,
    sceneSaveLoading = false,
    isAnySceneSaveLoading = false,
    frameGenerationLoading = {},
    slideGenerationLoading = {},
    videoGenerationLoading = {},
    aspectRatio,
    businessData,
    isSidebarOpen,
    onCloseSidebar,
    onOpenSidebar,
}) => {
    const [viewMode, setViewMode] = useState('Video');
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [isPropertiesMobileOpen, setIsPropertiesMobileOpen] = useState(false);
    const [imageErrors, setImageErrors] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ visible: false, title: '', content: '', onOk: null, okType: 'primary' });

    useEffect(() => { setIsDirty(false); }, [selectedSceneIndex]);

    // Build a map of group_id -> array of flat indices
    const groupMap = useMemo(() => {
        if (!scriptData?.scenes) return {};
        const map = {};
        scriptData.scenes.forEach((scene, idx) => {
            if (scene.group_id != null) {
                if (!map[scene.group_id]) map[scene.group_id] = [];
                map[scene.group_id].push(idx);
            }
        });
        return map;
    }, [scriptData?.scenes]);

    const getGroupInfo = (scene, index) => {
        if (scene.group_id == null) return null;
        const members = groupMap[scene.group_id] || [];
        const positionInGroup = members.indexOf(index);
        const isFirst = positionInGroup === 0;
        const isLast = positionInGroup === members.length - 1;
        const narration = scriptData.scenes[members[0]]?.group_narration || null;
        return { groupId: scene.group_id, members, positionInGroup, isFirst, isLast, narration, size: members.length };
    };

    const showConfirm = (title, content, onOk, okType = 'primary') => {
        setConfirmModal({ visible: true, title, content, onOk, okType });
    };

    const handleConfirmOk = () => {
        if (confirmModal.onOk) confirmModal.onOk();
        setConfirmModal({ ...confirmModal, visible: false });
    };

    const currentScene = scriptData?.scenes?.[selectedSceneIndex];
    const isSlideScene = currentScene?.type === 'slide';

    const updateCurrentScene = (field, value) => {
        if (!currentScene) return;
        setIsDirty(true);
        onLocalUpdate(selectedSceneIndex, { [field]: value });
    };

    const updateShotField = (field, value) => {
        const currentShot = currentScene?.shot || {};
        setIsDirty(true);
        onLocalUpdate(selectedSceneIndex, { shot: { ...currentShot, [field]: value } });
    };

    const handleEditChange = (editIndex, value) => {
        const newEdits = [...(currentScene.edits || [])];
        newEdits[editIndex] = value;
        setIsDirty(true);
        onLocalUpdate(selectedSceneIndex, { edits: newEdits });
    };

    const handleDeleteEdit = (editIndex) => {
        const newEdits = [...(currentScene.edits || [])];
        newEdits.splice(editIndex, 1);
        setIsDirty(true);
        onLocalUpdate(selectedSceneIndex, { edits: newEdits });
    };

    const handleAddEdit = () => {
        setIsDirty(true);
        onLocalUpdate(selectedSceneIndex, { edits: [...(currentScene.edits || []), ''] });
    };

    const handleSaveScene = async () => {
        if (sceneSaveLoading) return;
        const saved = await onSaveScene(selectedSceneIndex, currentScene);
        if (saved) {
            setIsDirty(false);
        }
    };

    const getAvailablePhotos = () => {
        if (!businessData?.photos) return [];
        return businessData.photos.filter(p => p.selected !== false);
    };

    const scenePhotoReference = currentScene?.photo_name_id || currentScene?.photo_id || null;

    const sourcePhotoOptions = useMemo(() => {
        const seen = new Set();

        return getAvailablePhotos()
            .filter((photo) => {
                const value = getPhotoReferenceValue(photo);
                if (!value || seen.has(value)) return false;
                seen.add(value);
                return true;
            })
            .map((photo, idx) => ({
                ...photo,
                value: getPhotoReferenceValue(photo),
                previewUrl: getPhotoImageUrl(photo),
                label: photo.caption?.trim() || `Photo ${idx + 1}`,
            }));
    }, [businessData?.photos]);

    const selectedSourcePhoto = sourcePhotoOptions.find(
        (photo) => getPhotoReferenceCandidates(photo).includes(scenePhotoReference)
    ) || null;

    const missingSourcePhotoValue = scenePhotoReference && !selectedSourcePhoto
        ? `${MISSING_SOURCE_PHOTO_PREFIX}${scenePhotoReference}`
        : null;

    const sourcePhotoSelectValue = selectedSourcePhoto?.value || missingSourcePhotoValue || '';

    const originalUrl = selectedSourcePhoto?.previewUrl || null;
    const frameUrl = currentScene?.image_url || currentScene?.initial_frame;
    const slideUrl = currentScene?.slide_url;
    const videoUrl = currentScene?.video_url;

    const hasNoScenes = !scriptData || !scriptData.scenes || scriptData.scenes.length === 0;

    if (hasNoScenes) {
        return (
            <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-background to-muted/50 p-6">
                <div
                    className="flex w-full max-w-[500px] items-center justify-center overflow-hidden rounded-2xl bg-slate-950 shadow-xl"
                    style={{ aspectRatio: aspectRatio === '9:16' ? '9/16' : '16/9' }}
                >
                    <div className="flex flex-col items-center justify-center px-6 py-10 text-center text-white sm:px-8">
                        <FileText className="mb-4 h-12 w-12 text-white/80" />
                        <h4 className="mb-2 text-lg font-semibold text-white">No Script Yet</h4>
                        <p className="mb-6 max-w-[280px] text-center text-white/80">
                            Generate an AI-powered script based on your creative brief
                        </p>
                        <Button size="lg" onClick={onGenerateScript} disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            <FileText className="mr-2 h-4 w-4" />
                            Generate Script
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const totalDuration = scriptData?.scenes?.reduce((sum, scene) => sum + (scene.duration || 0), 0) || 0;
    const sceneCount = scriptData?.scenes?.length || 0;
    const sceneLabel = isSlideScene ? 'Slide' : 'Scene';

    const isAnyGenerationRunning =
        frameGenerationLoading[selectedSceneIndex] ||
        slideGenerationLoading[selectedSceneIndex] ||
        videoGenerationLoading[selectedSceneIndex];

    const handleFrameAction = () => {
        const hasValidFrame = frameUrl && !imageErrors[`frame-${selectedSceneIndex}`];
        if (hasValidFrame) {
            showConfirm('Regenerate Frame?', 'The current frame will be replaced. This cannot be undone.', () => {
                setImageErrors((prev) => ({ ...prev, [`frame-${selectedSceneIndex}`]: false }));
                onGenerateFrame(selectedSceneIndex);
            });
            return;
        }

        setImageErrors((prev) => ({ ...prev, [`frame-${selectedSceneIndex}`]: false }));
        onGenerateFrame(selectedSceneIndex);
    };

    const handleSlideAction = () => {
        const hasValidSlide = slideUrl && !imageErrors[`slide-${selectedSceneIndex}`];
        if (hasValidSlide) {
            showConfirm('Regenerate Slide?', 'The current slide will be replaced. This cannot be undone.', () => {
                setImageErrors((prev) => ({ ...prev, [`slide-${selectedSceneIndex}`]: false }));
                onGenerateSlide(selectedSceneIndex);
            });
            return;
        }

        setImageErrors((prev) => ({ ...prev, [`slide-${selectedSceneIndex}`]: false }));
        onGenerateSlide(selectedSceneIndex);
    };

    const handleVideoAction = () => {
        if (videoUrl) {
            showConfirm('Regenerate Scene Video?', 'The current scene video will be replaced. This cannot be undone.', () => onGenerateSceneVideo(selectedSceneIndex));
            return;
        }

        onGenerateSceneVideo(selectedSceneIndex);
    };

    const renderMainVisual = () => {
        const isFrameLoading = frameGenerationLoading[selectedSceneIndex];
        const isSlideLoading = slideGenerationLoading[selectedSceneIndex];
        const isVideoLoading = videoGenerationLoading[selectedSceneIndex];

        switch (viewMode) {
            case 'Original':
                if (originalUrl && !imageErrors[`original-${selectedSceneIndex}`]) {
                    return <img src={originalUrl} alt="Original" className="w-full h-full object-contain" onError={() => setImageErrors(prev => ({ ...prev, [`original-${selectedSceneIndex}`]: true }))} />;
                }
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mb-4 opacity-50" />
                        <p>{originalUrl ? 'Original photo failed to load' : 'No original photo linked to this scene'}</p>
                    </div>
                );
            case 'Frame':
                if (isFrameLoading) {
                    return (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                            <Spinner size="lg" />
                            <p className="text-muted-foreground mt-4">Generating frame...</p>
                        </div>
                    );
                }
                if (frameUrl && !imageErrors[`frame-${selectedSceneIndex}`]) {
                    return <img src={frameUrl} alt="Initial Frame" className="w-full h-full object-contain" onError={() => setImageErrors(prev => ({ ...prev, [`frame-${selectedSceneIndex}`]: true }))} />;
                }
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mb-4 opacity-50" />
                        <p>{frameUrl ? 'Frame failed to load' : 'No initial frame generated'}</p>
                        <Button variant="outline" className="mt-4" onClick={() => {
                            setImageErrors(prev => ({ ...prev, [`frame-${selectedSceneIndex}`]: false }));
                            onGenerateFrame(selectedSceneIndex);
                        }}>{frameUrl ? 'Regenerate Frame' : 'Generate Frame'}</Button>
                    </div>
                );
            case 'Slide':
                if (isSlideLoading) {
                    return (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                            <Spinner size="lg" />
                            <p className="text-muted-foreground mt-4">Generating slide...</p>
                        </div>
                    );
                }
                if (slideUrl && !imageErrors[`slide-${selectedSceneIndex}`]) {
                    return <img src={slideUrl} alt="Slide" className="w-full h-full object-contain" onError={() => setImageErrors(prev => ({ ...prev, [`slide-${selectedSceneIndex}`]: true }))} />;
                }
                return (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mb-4 opacity-50" />
                        <p>{slideUrl ? 'Slide failed to load' : 'No slide generated'}</p>
                        {isSlideScene && onGenerateSlide && (
                            <Button variant="outline" className="mt-4" onClick={() => {
                                setImageErrors(prev => ({ ...prev, [`slide-${selectedSceneIndex}`]: false }));
                                onGenerateSlide(selectedSceneIndex);
                            }}>{slideUrl ? 'Regenerate Slide' : 'Generate Slide'}</Button>
                        )}
                    </div>
                );
            case 'Video':
            default:
                if (isVideoLoading) {
                    return (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                            <Spinner size="lg" />
                            <p className="text-muted-foreground mt-4">Generating video...</p>
                        </div>
                    );
                }
                return videoUrl ? (
                    <video src={videoUrl} controls className="w-full h-full" />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                        <Video className="h-12 w-12 mb-4 opacity-50" />
                        <p>No video generated</p>
                        {onGenerateSceneVideo && (
                            <Button variant="outline" className="mt-4" onClick={() => onGenerateSceneVideo(selectedSceneIndex)}>Generate Video</Button>
                        )}
                    </div>
                );
        }
    };

    const renderEditPanel = () => {
        if (!currentScene) {
            return (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                    Select a scene to edit
                </div>
            );
        }

        return (
            <div className="flex h-full flex-col bg-card">
                <div className="flex items-center justify-between border-b bg-muted/50 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="hidden h-7 w-7 lg:inline-flex"
                            onClick={() => setIsPanelCollapsed(true)}
                            aria-label="Collapse properties panel"
                        >
                            <PanelLeftClose className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 lg:hidden"
                            onClick={() => setIsPropertiesMobileOpen(false)}
                            aria-label="Close properties panel"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                        <span className="text-sm font-semibold text-foreground">
                            {isSlideScene ? 'Slide' : 'Scene'} {selectedSceneIndex + 1} Properties
                        </span>
                        {isSlideScene ? (
                            <Badge className="ml-2 border-purple-200 bg-purple-50 text-purple-700">Slide</Badge>
                        ) : null}
                        {currentScene?.group_id != null ? (
                            <Badge className="ml-1 border-orange-200 bg-orange-50 text-orange-700">Grouped</Badge>
                        ) : null}
                    </div>
                    <Button size="sm" onClick={handleSaveScene} disabled={sceneSaveLoading || saveLoading}>
                        {sceneSaveLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                        {sceneSaveLoading ? 'Saving...' : 'Save'}
                    </Button>
                </div>

                {isDirty ? (
                    <Alert variant="warning" className="mx-4 mt-3 border-amber-300 bg-amber-50">
                        <AlertDescription className="text-xs text-amber-700">
                            You have unsaved changes. They will not take effect until you save them.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {currentScene?.group_id != null && (() => {
                    const group = getGroupInfo(currentScene, selectedSceneIndex);
                    return group?.narration ? (
                        <div className="mx-4 mt-3 rounded-md border border-orange-200 bg-orange-50 p-3">
                            <div className="mb-1 flex items-center gap-1.5">
                                <Group className="h-3 w-3 text-orange-700" />
                                <span className="text-[11px] font-semibold text-orange-700">Group Narration</span>
                                <span className="text-[10px] text-orange-600">
                                    (scenes {group.members.map(i => i + 1).join(', ')})
                                </span>
                            </div>
                            <p className="text-xs leading-relaxed text-orange-900">
                                "{group.narration}"
                            </p>
                        </div>
                    ) : null;
                })()}

                <div className="flex-1 space-y-5 overflow-y-auto p-5">
                        {/* Duration & Source Photo */}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Duration</label>
                                <Select value={String(currentScene.duration)} onValueChange={(val) => updateCurrentScene('duration', parseInt(val))}>
                                    <SelectTrigger className="h-10 text-xs sm:h-8">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">1 seconds</SelectItem>
                                        <SelectItem value="2">2 seconds</SelectItem>
                                        <SelectItem value="3">3 seconds</SelectItem>
                                        <SelectItem value="4">4 seconds</SelectItem>
                                        <SelectItem value="5">5 seconds</SelectItem>
                                        <SelectItem value="6">6 seconds</SelectItem>
                                        <SelectItem value="7">7 seconds</SelectItem>
                                        <SelectItem value="8">8 seconds</SelectItem>
                                        <SelectItem value="9">9 seconds</SelectItem>
                                        <SelectItem value="10">10 seconds</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Source Photo</label>
                                <Select
                                    value={sourcePhotoSelectValue}
                                    onValueChange={(value) => {
                                        if (value.startsWith(MISSING_SOURCE_PHOTO_PREFIX)) return;
                                        updateCurrentScene('photo_name_id', value);
                                    }}
                                >
                                    <SelectTrigger className="h-10 w-full text-xs sm:h-8">
                                        <SelectValue placeholder="Select photo" />
                                    </SelectTrigger>
                                    <SelectContent
                                        position="popper"
                                        align="start"
                                        className="w-[var(--radix-select-trigger-width)] max-w-[min(24rem,calc(100vw-2rem))]"
                                    >
                                        {missingSourcePhotoValue && (
                                            <SelectItem value={missingSourcePhotoValue}>
                                                <div className="flex min-w-0 flex-col items-start gap-0.5">
                                                    <span className="truncate max-w-full">Missing photo reference</span>
                                                    <span className="max-w-full truncate text-[11px] text-muted-foreground">
                                                        Pick a replacement from the list below.
                                                    </span>
                                                </div>
                                            </SelectItem>
                                        )}
                                        {sourcePhotoOptions.map((photo) => (
                                            <SelectItem key={photo.value} value={photo.value}>
                                                <div className="flex min-w-0 items-center gap-2">
                                                    {photo.previewUrl && (
                                                        <img src={photo.previewUrl} alt="" className="w-6 h-6 object-cover rounded-sm bg-muted" />
                                                    )}
                                                    <span className="min-w-0 flex-1 truncate">{photo.label}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {missingSourcePhotoValue && (
                                    <p className="mt-1 text-[11px] text-amber-700">
                                        This scene references a photo that is no longer available. Choose a replacement.
                                    </p>
                                )}
                            </div>
                        </div>

                        <Separator />

                        {/* Scene Type & Provider badges */}
                        {(currentScene.scene_type || currentScene.video_provider) && (
                            <div className="flex flex-wrap gap-1.5">
                                {currentScene.scene_type && (
                                    <Badge variant="secondary" className="text-[10px]">
                                        {currentScene.scene_type.replace(/_/g, ' ')}
                                    </Badge>
                                )}
                                {currentScene.video_provider && (
                                    <Badge variant="outline" className="text-[10px]">
                                        {currentScene.video_provider}
                                    </Badge>
                                )}
                            </div>
                        )}

                        {/* Screenplay Sub-fields */}
                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-1 mb-2">
                            Screenplay
                        </h4>

                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Subject Action</label>
                            <Textarea
                                value={currentScene.subject_action || ''}
                                onChange={(e) => updateCurrentScene('subject_action', e.target.value)}
                                rows={2}
                                placeholder="What the subject does in this scene..."
                                className="text-sm"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Environment Motion</label>
                            <Input
                                value={currentScene.environment_motion || ''}
                                onChange={(e) => updateCurrentScene('environment_motion', e.target.value)}
                                placeholder="Background movement, ambient motion..."
                                className="h-10 text-sm sm:h-8"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Atmospheric Detail</label>
                            <Input
                                value={currentScene.atmospheric_detail || ''}
                                onChange={(e) => updateCurrentScene('atmospheric_detail', e.target.value)}
                                placeholder="Lighting, mood, ambience..."
                                className="h-10 text-sm sm:h-8"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Narration</label>
                            <Textarea
                                value={currentScene.narration || ''}
                                onChange={(e) => updateCurrentScene('narration', e.target.value)}
                                rows={2}
                                placeholder="Voice-over narration text..."
                                className="text-sm"
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div>
                                <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Action Timing</label>
                                <Input
                                    value={currentScene.action_timing || ''}
                                    onChange={(e) => updateCurrentScene('action_timing', e.target.value)}
                                    placeholder="e.g., slow reveal"
                                    className="h-10 text-sm sm:h-8"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Mood Direction</label>
                                <Input
                                    value={currentScene.mood_direction || ''}
                                    onChange={(e) => updateCurrentScene('mood_direction', e.target.value)}
                                    placeholder="e.g., warm, inviting"
                                    className="h-10 text-sm sm:h-8"
                                />
                            </div>
                        </div>

                        <Separator />

                        {/* Audio */}
                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Audio Description</label>
                            <Textarea
                                value={currentScene.audio || ''}
                                onChange={(e) => updateCurrentScene('audio', e.target.value)}
                                rows={2}
                                placeholder="Music, sound effects..."
                                className="text-sm"
                            />
                        </div>

                        {/* Audio Constraints */}
                        <div>
                            <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Audio Constraints</label>
                            <Input
                                value={currentScene.audio_constraints || ''}
                                onChange={(e) => updateCurrentScene('audio_constraints', e.target.value)}
                                placeholder="e.g., 'No singing'"
                                className="h-10 text-sm sm:h-8"
                            />
                        </div>

                        {/* Slide Content */}
                        {isSlideScene && (
                            <>
                                <Separator />
                                <div>
                                    <label className="text-xs font-semibold tracking-wide text-muted-foreground block mb-1">Slide Content</label>
                                    <Textarea
                                        value={currentScene.content || ''}
                                        onChange={(e) => updateCurrentScene('content', e.target.value)}
                                        rows={3}
                                        placeholder="Text content for this slide..."
                                        className="text-sm"
                                    />
                                </div>

                                {/* Slide Offer */}
                                {currentScene.offer && (
                                    <div className="p-3 bg-muted/50 rounded-md space-y-2">
                                        <label className="text-xs font-semibold tracking-wide text-muted-foreground block">Offer</label>
                                        <Input
                                            value={currentScene.offer?.headline || ''}
                                            onChange={(e) => updateCurrentScene('offer', { ...currentScene.offer, headline: e.target.value })}
                                            placeholder="Offer headline"
                                            className="h-10 text-sm sm:h-8"
                                        />
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            <Input
                                                value={currentScene.offer?.code || ''}
                                                onChange={(e) => updateCurrentScene('offer', { ...currentScene.offer, code: e.target.value })}
                                                placeholder="Promo code"
                                                className="h-10 text-sm sm:h-8"
                                            />
                                            <Input
                                                value={currentScene.offer?.expiry || ''}
                                                onChange={(e) => updateCurrentScene('offer', { ...currentScene.offer, expiry: e.target.value })}
                                                placeholder="Expiry"
                                                className="h-10 text-sm sm:h-8"
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-2 mb-3">
                            Image Edits
                        </h4>

                        {/* Image Edits */}
                        <div>
                            {(currentScene.edits || []).map((edit, i) => (
                                <div key={i} className="flex gap-2 mb-2">
                                    <Input
                                        value={edit}
                                        onChange={(e) => handleEditChange(i, e.target.value)}
                                        placeholder="Edit instruction..."
                                        className="h-10 flex-1 text-sm sm:h-8"
                                    />
                                    <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-destructive sm:h-8 sm:w-8" onClick={() => handleDeleteEdit(i)} aria-label={`Delete edit ${i + 1}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                            <Button variant="outline" size="sm" className="w-full border-dashed" onClick={handleAddEdit}>
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                Add Edit
                            </Button>
                        </div>

                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-2 mb-3">
                            Shot Details
                        </h4>

                        {/* Shot Details */}
                        <div className="grid grid-cols-1 gap-x-2 gap-y-3 sm:grid-cols-2">
                            {[
                                { key: 'framing_scale', label: 'Framing Scale', placeholder: 'Medium Shot' },
                                { key: 'camera_angle_elevation', label: 'Camera Angle', placeholder: 'Eye level' },
                                { key: 'camera_motion', label: 'Camera Motion', placeholder: 'Slow pan right' },
                                { key: 'perspective_point_of_view', label: 'Point of View', placeholder: "Diner's POV" },
                                { key: 'lens_optical_qualities', label: 'Lens Qualities', placeholder: 'Sharp focus' },
                                { key: 'purpose_narrative_function', label: 'Narrative Purpose', placeholder: 'Show quality' },
                            ].map(({ key, label, placeholder }) => (
                                <div key={key}>
                                    <label className="text-[11px] text-muted-foreground block mb-0.5">{label}</label>
                                    <Input
                                        value={currentScene.shot?.[key] || ''}
                                        onChange={(e) => updateShotField(key, e.target.value)}
                                        className="h-10 text-sm sm:h-8"
                                        placeholder={placeholder}
                                    />
                                </div>
                            ))}
                        </div>
                </div>
            </div>
        );
    };

    const viewOptions = isSlideScene ? ['Original', 'Frame', 'Slide', 'Video'] : ['Original', 'Frame', 'Video'];
    const activeView = viewMode === 'Slide' && !isSlideScene ? 'Video' : viewMode;
    const sidebarContent = (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
            <div className="min-w-0 border-b border-border p-4">
                <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 lg:hidden"
                            onClick={onCloseSidebar}
                            aria-label="Close story stream"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                        <h5 className="text-base font-semibold text-foreground">Story Stream</h5>
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" disabled aria-label="Add scene (coming soon)">
                                <Plus className="h-4 w-4 text-muted-foreground" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Coming soon</TooltipContent>
                    </Tooltip>
                </div>

                <div className="mb-3 min-w-0 rounded-lg bg-muted/50 p-3">
                    <div className="mb-2 flex min-w-0 items-start justify-between gap-2">
                        <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                            {sceneCount} scenes • {totalDuration}s total
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => showConfirm(
                                        'Regenerate Script?',
                                        'The entire script will be regenerated. All current scene details, edits, and generated frames will be lost. This action cannot be undone.',
                                        onGenerateScript,
                                        'danger'
                                    )}
                                    disabled={loading}
                                >
                                    <RotateCw className={cn('h-3.5 w-3.5 text-muted-foreground', loading && 'animate-spin')} />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Regenerate entire script</TooltipContent>
                        </Tooltip>
                    </div>
                    {scriptData.concept ? (
                        <p className="truncate text-xs text-muted-foreground">
                            {typeof scriptData.concept === 'string'
                                ? `${scriptData.concept.substring(0, 100)}${scriptData.concept.length > 100 ? '...' : ''}`
                                : ''}
                        </p>
                    ) : null}
                </div>

                {businessData?.brand_identity ? (
                    <div className="min-w-0 rounded-lg bg-muted/50 p-3">
                        <div className="mb-2 flex items-center gap-2">
                            <Palette className="h-4 w-4 text-coral" />
                            <span className="text-xs font-semibold text-foreground">Brand Identity</span>
                        </div>
                        {businessData.brand_identity.logo_url ? (
                            <div className="mb-2">
                                <img
                                    src={businessData.brand_identity.logo_url}
                                    alt="Business Logo"
                                    className="max-h-[40px] max-w-[80px] rounded border bg-white p-1 object-contain"
                                />
                            </div>
                        ) : null}
                        {businessData.brand_identity.primary_colors?.length > 0 ? (
                            <div className="mb-2">
                                <span className="mb-1 block text-[10px] text-muted-foreground">Colors</span>
                                <div className="flex gap-1">
                                    {businessData.brand_identity.primary_colors.map((color, idx) => (
                                        <Tooltip key={idx}>
                                            <TooltipTrigger asChild>
                                                <div className="h-5 w-5 rounded border" style={{ background: color }} />
                                            </TooltipTrigger>
                                            <TooltipContent>{color}</TooltipContent>
                                        </Tooltip>
                                    ))}
                                    {businessData.brand_identity.secondary_colors?.map((color, idx) => (
                                        <Tooltip key={`sec-${idx}`}>
                                            <TooltipTrigger asChild>
                                                <div className="h-5 w-5 rounded border opacity-70" style={{ background: color }} />
                                            </TooltipTrigger>
                                            <TooltipContent>{color}</TooltipContent>
                                        </Tooltip>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        {businessData.brand_identity.mood ? (
                            <p className="text-xs text-muted-foreground">{businessData.brand_identity.mood}</p>
                        ) : null}
                        {businessData.brand_identity.mood_keywords?.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                                {businessData.brand_identity.mood_keywords.slice(0, 4).map((keyword, idx) => (
                                    <Badge key={idx} variant="secondary" className="px-1.5 py-0 text-[10px]">{keyword}</Badge>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="flex-1 overflow-x-hidden overflow-y-auto p-4">
                {scriptData?.scenes?.map((scene, index) => {
                    const group = getGroupInfo(scene, index);
                    const isGrouped = group != null;

                    return (
                        <React.Fragment key={index}>
                            {isGrouped && group.isFirst ? (
                                <div className="mb-0.5 ml-3 mt-1 flex min-w-0 items-center gap-1.5 rounded-tr-md border-l-[3px] border-l-coral bg-muted/50 px-3 py-1.5">
                                    <Group className="h-3 w-3 shrink-0 text-muted-foreground" />
                                    <span className="shrink-0 text-[11px] font-semibold text-muted-foreground">
                                        Group ({group.size} scenes)
                                    </span>
                                    {group.narration ? (
                                        <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground/70">
                                            — {group.narration}
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}

                            <div
                                onClick={() => {
                                    setSelectedSceneIndex(index);
                                    onCloseSidebar?.();
                                }}
                                className={cn(
                                    'flex min-w-0 cursor-pointer items-start gap-3 overflow-hidden border p-3 transition-all',
                                    isGrouped
                                        ? 'ml-3 rounded-none rounded-r-lg border-l-[3px] border-l-coral'
                                        : 'rounded-lg',
                                    isGrouped && !group.isLast ? 'mb-0.5' : 'mb-2',
                                    selectedSceneIndex === index
                                        ? 'border-coral bg-muted'
                                        : 'border-transparent hover:bg-muted/50',
                                    isGrouped && selectedSceneIndex !== index && 'border-l-coral'
                                )}
                            >
                                <div
                                    className="shrink-0 overflow-hidden rounded border bg-muted"
                                    style={{
                                        width: 80,
                                        aspectRatio: aspectRatio === '9:16' ? '9/16' : '16/9',
                                    }}
                                >
                                    {scene.image_url && !imageErrors[`thumb-${index}`] ? (
                                        <img
                                            src={scene.image_url}
                                            alt={`Scene ${index + 1}`}
                                            className="h-full w-full object-cover"
                                            onError={() => setImageErrors((prev) => ({ ...prev, [`thumb-${index}`]: true }))}
                                        />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                            <ImageIcon className="h-4 w-4" />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1 overflow-hidden">
                                    <div className="flex min-w-0 items-center justify-between gap-2">
                                        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                                            <span className={cn(
                                                'min-w-0 truncate text-sm font-bold',
                                                selectedSceneIndex === index ? 'text-coral' : 'text-foreground'
                                            )}
                                            >
                                                {scene.type === 'slide' ? 'Slide' : 'Scene'} {index + 1}
                                            </span>
                                            {scene.scene_type ? (
                                                <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
                                                    {scene.scene_type.replace(/_/g, ' ')}
                                                </Badge>
                                            ) : scene.type ? (
                                                <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">{scene.type}</Badge>
                                            ) : null}
                                        </div>
                                        <span className="shrink-0 text-xs text-muted-foreground">{scene.duration}s</span>
                                    </div>
                                    <p className="mt-1 truncate text-xs text-muted-foreground">
                                        {scene.subject_action || scene.screenplay || 'No action'}
                                    </p>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}

            </div>

            <div className="border-t border-border bg-card p-4">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="outline" className="w-full border-dashed text-muted-foreground" disabled>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Scene
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Coming soon</TooltipContent>
                </Tooltip>

                {onSaveAllScenes ? (
                    <Button className="mt-3 h-10 w-full" onClick={onSaveAllScenes} disabled={saveLoading || isAnySceneSaveLoading}>
                        {saveLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Save All Scenes
                    </Button>
                ) : null}
            </div>
        </div>
    );

    const stageContent = scriptData?.scenes?.length > 0 && currentScene ? (
        <div className="flex h-full w-full flex-col">
            <div className="hidden h-12 shrink-0 items-center justify-between gap-2 overflow-x-auto border-b border-border bg-card/95 px-4 backdrop-blur-sm lg:flex">
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedSceneIndex(Math.max(0, selectedSceneIndex - 1))} disabled={selectedSceneIndex === 0} aria-label="Previous scene">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-1 text-sm font-medium text-foreground">
                        {sceneLabel} {selectedSceneIndex + 1}
                    </span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedSceneIndex(Math.min(scriptData.scenes.length - 1, selectedSceneIndex + 1))} disabled={selectedSceneIndex === scriptData.scenes.length - 1} aria-label="Next scene">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <ToggleGroup type="single" size="sm" value={activeView} onValueChange={(value) => value && setViewMode(value)} className="ml-2">
                        {viewOptions.map((option) => (
                            <ToggleGroupItem key={option} value={option} className="px-2.5 text-xs">
                                {option}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>

                <div className="flex items-center gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={handleFrameAction}
                                disabled={(isAnyGenerationRunning && !frameGenerationLoading[selectedSceneIndex]) || frameGenerationLoading[selectedSceneIndex]}
                            >
                                {frameGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{frameUrl ? 'Regenerate Frame' : 'Generate Frame'}</TooltipContent>
                    </Tooltip>

                    {isSlideScene && onGenerateSlide ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={handleSlideAction}
                                    disabled={(isAnyGenerationRunning && !slideGenerationLoading[selectedSceneIndex]) || slideGenerationLoading[selectedSceneIndex]}
                                >
                                    {slideGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{slideUrl ? 'Regenerate Slide' : 'Generate Slide'}</TooltipContent>
                        </Tooltip>
                    ) : null}

                    {onGenerateSceneVideo ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={videoUrl ? 'outline' : 'default'}
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={handleVideoAction}
                                    disabled={(isAnyGenerationRunning && !videoGenerationLoading[selectedSceneIndex]) || videoGenerationLoading[selectedSceneIndex]}
                                >
                                    {videoGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{videoUrl ? 'Regenerate Scene Video' : 'Generate Scene Video'}</TooltipContent>
                        </Tooltip>
                    ) : null}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="hidden h-7 w-7 lg:inline-flex"
                                onClick={() => setIsPanelCollapsed((collapsed) => !collapsed)}
                                aria-label={isPanelCollapsed ? 'Show properties' : 'Hide properties'}
                            >
                                {isPanelCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{isPanelCollapsed ? 'Show Properties' : 'Hide Properties'}</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 lg:hidden"
                                onClick={() => setIsPropertiesMobileOpen(true)}
                                aria-label="Open properties"
                            >
                                <PanelLeftOpen className="h-4 w-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Open Properties</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            <div className="border-b border-border bg-card/95 px-3 py-2.5 backdrop-blur-sm lg:hidden">
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                                Current scene
                            </p>
                            <div className="mt-1 flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-semibold text-foreground">
                                    {sceneLabel} {selectedSceneIndex + 1}
                                </span>
                                <Badge variant="secondary" className="shrink-0 px-2 py-0.5 text-[10px]">
                                    {currentScene.duration || 0}s
                                </Badge>
                            </div>
                        </div>
                        <span className="shrink-0 text-xs text-muted-foreground">
                            {selectedSceneIndex + 1} of {sceneCount}
                        </span>
                    </div>

                    <div className="grid grid-cols-[auto_auto_minmax(0,1fr)] gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-xl px-3"
                            onClick={() => onOpenSidebar?.()}
                        >
                            <List className="h-4 w-4" />
                            Story
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 rounded-xl px-3"
                            onClick={() => setIsPropertiesMobileOpen(true)}
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            Details
                        </Button>
                        <Button
                            size="sm"
                            className="h-9 rounded-xl px-3"
                            onClick={handleSaveScene}
                            disabled={sceneSaveLoading || saveLoading}
                        >
                            {sceneSaveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Save
                        </Button>
                    </div>

                    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-2xl border border-border bg-muted/30 p-1.5">
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setSelectedSceneIndex(Math.max(0, selectedSceneIndex - 1))} disabled={selectedSceneIndex === 0} aria-label="Previous scene">
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <div className="min-w-0 px-1 text-center">
                            <p className="truncate text-sm font-medium text-foreground">
                                {currentScene.scene_type
                                    ? currentScene.scene_type.replace(/_/g, ' ')
                                    : currentScene.type || `${sceneLabel} view`}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                                {currentScene.subject_action || currentScene.screenplay || 'Review the current scene details'}
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={() => setSelectedSceneIndex(Math.min(scriptData.scenes.length - 1, selectedSceneIndex + 1))} disabled={selectedSceneIndex === scriptData.scenes.length - 1} aria-label="Next scene">
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted p-3 sm:p-6 sm:pb-8">
                <div
                    style={{
                        aspectRatio: aspectRatio === '9:16' ? '9/16' : '16/9',
                        ...(aspectRatio === '9:16'
                            ? { height: '100%', width: 'auto', maxHeight: 'calc(100% - 32px)' }
                            : { width: '100%', height: 'auto', maxWidth: 'calc(100% - 32px)' }
                        ),
                    }}
                    className="relative overflow-hidden rounded-[20px] border bg-white shadow-xl"
                >
                    {renderMainVisual()}
                    <div className="absolute left-4 top-4">
                        <div className="rounded bg-black/60 px-2 py-1 text-xs uppercase text-white">
                            {viewMode}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-border bg-card/95 px-3 py-3 backdrop-blur-sm lg:hidden">
                <div className="flex flex-col gap-3">
                    <div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <div className="flex min-w-max items-center gap-2">
                            {viewOptions.map((option) => (
                                <Button
                                    key={option}
                                    type="button"
                                    variant={activeView === option ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-9 rounded-xl px-4"
                                    onClick={() => setViewMode(option)}
                                >
                                    {option}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-10 justify-start rounded-xl px-3"
                            onClick={handleFrameAction}
                            disabled={(isAnyGenerationRunning && !frameGenerationLoading[selectedSceneIndex]) || frameGenerationLoading[selectedSceneIndex]}
                        >
                            {frameGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                            {frameUrl ? 'Regenerate frame' : 'Generate frame'}
                        </Button>

                        {isSlideScene && onGenerateSlide ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-10 justify-start rounded-xl px-3"
                                onClick={handleSlideAction}
                                disabled={(isAnyGenerationRunning && !slideGenerationLoading[selectedSceneIndex]) || slideGenerationLoading[selectedSceneIndex]}
                            >
                                {slideGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                                {slideUrl ? 'Regenerate slide' : 'Generate slide'}
                            </Button>
                        ) : null}

                        {onGenerateSceneVideo ? (
                            <Button
                                variant={videoUrl ? 'outline' : 'default'}
                                size="sm"
                                className={cn(
                                    'h-10 justify-start rounded-xl px-3',
                                    !isSlideScene && 'sm:col-span-2',
                                )}
                                onClick={handleVideoAction}
                                disabled={(isAnyGenerationRunning && !videoGenerationLoading[selectedSceneIndex]) || videoGenerationLoading[selectedSceneIndex]}
                            >
                                {videoGenerationLoading[selectedSceneIndex] ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                                {videoUrl ? 'Regenerate video' : 'Generate video'}
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <p>Select a scene to edit</p>
        </div>
    );

    return (
        <TooltipProvider>
            <DirectorStudioLayout
                sidebar={sidebarContent}
                stage={stageContent}
                properties={renderEditPanel()}
                sidebarMobileOpen={isSidebarOpen}
                onSidebarMobileOpenChange={(open) => {
                    if (!open) onCloseSidebar?.();
                }}
                propertiesMobileOpen={isPropertiesMobileOpen}
                onPropertiesMobileOpenChange={setIsPropertiesMobileOpen}
                propertiesCollapsed={isPanelCollapsed}
            />

            {/* Confirmation Dialog */}
            <Dialog open={confirmModal.visible} onOpenChange={(v) => !v && setConfirmModal({ ...confirmModal, visible: false })}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>{confirmModal.title}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">{confirmModal.content}</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmModal({ ...confirmModal, visible: false })}>Cancel</Button>
                        <Button
                            variant={confirmModal.okType === 'danger' ? 'destructive' : 'default'}
                            onClick={handleConfirmOk}
                        >
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </TooltipProvider>
    );
};

export default StudioPhase;
